import { Divider } from '@mui/material';
import { useMemo } from 'react';
import { generatePath } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Contract_Status_Enum, Product_Family_Name_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { productSubFamilyMap } from '@kheops/utils';
import { RoutePaths } from '../../routes/AppRoutes';
import { ContractPreview } from '../../hooks/useContracts';
import Breadcrumb from '../../common/components/BreadCrumb';
import BackButton from '../../common/components/BackButton';
import { PackagingFamilyTypeIntl, ProductFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import { currentContextAtom } from '../../state';
import { Product } from './PackagingPage';

export interface PackagingPageBreadcrumbProps {
  product?: Product;
  contract?: ContractPreview;
}

export default function PackagingPageBreadcrumb({ product, contract }: PackagingPageBreadcrumbProps): React.JSX.Element {
  const { t } = useTranslation(['product-family']);
  const context = useAtomValue(currentContextAtom);

  const breadCrumb = useMemo(() => {
    if (!product) {
      return { backButtonFallback: '', breadCrumbItems: [] };
    }

    if (context.realm === Business_Profile_Enum.Buyer) {
      if (contract?.status === Contract_Status_Enum.Signed) {
        const supplierLink = generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: contract.supplyingCompanyId });

        return {
          backButtonFallback: supplierLink,
          breadCrumbItems: [
            { label: product.company.tradeName, to: supplierLink },
            {
              label: t(`product-family:${PackagingFamilyTypeIntl[product.sub_family as Product_Sub_Family_Name_Enum]}`),
              to: supplierLink,
              options: {
                state: {
                  productFamily: productSubFamilyMap[product.sub_family as Product_Sub_Family_Name_Enum],
                  productSubFamily: product.sub_family as Product_Sub_Family_Name_Enum,
                },
              },
            },
            { label: product.name },
          ],
        };
      }

      return {
        backButtonFallback: RoutePaths.DISCOVERY_SUPPLIERS,
        breadCrumbItems: [
          { label: t('common:all_products'), to: RoutePaths.DISCOVERY_SUPPLIERS },
          {
            label: t(`product-family:${ProductFamilyTypeIntl[productSubFamilyMap[product.sub_family as Product_Sub_Family_Name_Enum] as Product_Family_Name_Enum]}`),
            to: RoutePaths.DISCOVERY_SUPPLIERS,
            options: {
              state: {
                productFamily: productSubFamilyMap[product.sub_family as Product_Sub_Family_Name_Enum],
              },
            },
          },
          {
            label: t(`product-family:${PackagingFamilyTypeIntl[product.sub_family as Product_Sub_Family_Name_Enum]}`),
            to: RoutePaths.DISCOVERY_SUPPLIERS,
            options: {
              state: {
                productFamily: productSubFamilyMap[product.sub_family as Product_Sub_Family_Name_Enum],
                productSubFamily: product.sub_family as Product_Sub_Family_Name_Enum,
              },
            },
          },
          { label: product.name },
        ],
      };
    }

    const buyerCompanyPageOrFallback = contract
      ? generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: contract.buyingCompanyId })
      : RoutePaths.MY_COMPANY;

    return {
      backButtonFallback: RoutePaths.CONTRACT_LIST,
      breadCrumbItems: [
        { label: t('common:all_products'), to: buyerCompanyPageOrFallback },
        {
          label: t(`product-family:${PackagingFamilyTypeIntl[product.sub_family as Product_Sub_Family_Name_Enum]}`),
          to: buyerCompanyPageOrFallback,
          options: {
            state: {
              productFamily: productSubFamilyMap[product.sub_family as Product_Sub_Family_Name_Enum],
              productSubFamily: product.sub_family as Product_Sub_Family_Name_Enum,
            },
          },
        },
        { label: product.name },
      ],
    };
  }, [product]);

  return (
    <>
      <BackButton
        to={breadCrumb.backButtonFallback}
        asLink
      />
      <Divider
        orientation="vertical"
        sx={{
          height: '8px',
          mx: '8px',
        }}
      />
      <Breadcrumb items={breadCrumb.breadCrumbItems} />
    </>
  );
}
